"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toForwardSlash = toForwardSlash;
exports.normalizePathForCompare = normalizePathForCompare;
exports.basenameWithoutExtension = basenameWithoutExtension;
exports.dirnameForward = dirnameForward;
exports.isSameOrDescendantDir = isSameOrDescendantDir;
exports.buildProjectDefinition = buildProjectDefinition;
exports.buildBlobMap = buildBlobMap;
exports.projectDisplayName = projectDisplayName;
exports.endsWithExt = endsWithExt;
exports.endsWithAnyExt = endsWithAnyExt;
exports.detectAnchoredProjects = detectAnchoredProjects;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
function toForwardSlash(p) {
    return p.replace(/\\/g, '/');
}
function normalizePathForCompare(p) {
    return sigma_engine_shared_1.Files.normalizePath(toForwardSlash(p)).toLowerCase();
}
function basenameWithoutExtension(relativePath) {
    const base = sigma_engine_shared_1.Files.basenamePath(toForwardSlash(relativePath));
    const dot = base.lastIndexOf('.');
    return dot > 0 ? base.substring(0, dot) : base;
}
function dirnameForward(relativePath) {
    return sigma_engine_shared_1.Files.dirnamePath(toForwardSlash(relativePath));
}
function isSameOrDescendantDir(child, ancestor) {
    const c = normalizePathForCompare(child);
    const a = normalizePathForCompare(ancestor);
    if (c === a) {
        return true;
    }
    if (a === '' || a === '.') {
        return true;
    }
    return c.startsWith(`${a}/`);
}
function buildProjectDefinition(type, name, fileDetails, blobMap) {
    const otherFiles = {};
    const definition = {
        type,
        name,
        boards: {},
        schematics: {},
        other: otherFiles,
    };
    for (const detail of fileDetails) {
        const isClaimedAuxiliaryFailure = detail.semanticType === 'other' &&
            detail.fileValidationResult?.isValid === false &&
            detail.fileValidationResult.claimedTools?.includes(type) === true;
        if ((detail.severity === sigma_engine_shared_1.BoundaryLevel.Error && !isClaimedAuxiliaryFailure) || !detail.semanticType) {
            continue;
        }
        const contents = blobMap.get(detail.relativePath);
        if (!contents) {
            continue;
        }
        const descriptor = {
            type: detail.semanticType,
            originalFileName: detail.relativePath,
            contents,
            validationResult: detail.fileValidationResult,
        };
        const fileName = sigma_engine_shared_1.Files.basenamePath(toForwardSlash(detail.relativePath));
        switch (detail.semanticType) {
            case 'board':
                definition.boards[fileName] = descriptor;
                break;
            case 'schematic':
                definition.schematics[detail.relativePath] = { ...descriptor };
                break;
            default:
                otherFiles[detail.relativePath] = descriptor;
                break;
        }
    }
    return definition;
}
function buildBlobMap(files) {
    return new Map(files.map((f) => [f.relativePath, f.contents]));
}
function projectDisplayName(anchorRelativePath) {
    const base = basenameWithoutExtension(anchorRelativePath);
    const dir = dirnameForward(anchorRelativePath);
    if (dir === '' || dir === '.') {
        return base;
    }
    return `${dir}/${base}`;
}
function endsWithExt(ext) {
    const lower = ext.toLowerCase();
    return (relativePath) => relativePath.toLowerCase().endsWith(lower);
}
function endsWithAnyExt(exts) {
    const lowered = exts.map((e) => e.toLowerCase());
    return (relativePath) => {
        const lower = relativePath.toLowerCase();
        return lowered.some((e) => lower.endsWith(e));
    };
}
function detectAnchoredProjects(params) {
    const { tool, toolDisplayName, files, fileDetails, primaryAnchorMatch, primaryAnchorLabel, secondaryAnchorMatch, secondaryAnchorLabel, } = params;
    const toolFiles = fileDetails.filter((d) => d.cadTool === tool ||
        (d.semanticType === 'other' &&
            d.fileValidationResult?.isValid === false &&
            d.fileValidationResult.claimedTools?.includes(tool) === true));
    const warnings = [];
    const blobMap = buildBlobMap(files);
    if (toolFiles.length === 0) {
        return { projects: [], warnings };
    }
    let anchors = toolFiles.filter((d) => primaryAnchorMatch(d.relativePath));
    if (anchors.length === 0 && secondaryAnchorMatch) {
        const fallback = toolFiles.filter((d) => secondaryAnchorMatch(d.relativePath));
        if (fallback.length > 0) {
            warnings.push(`No ${primaryAnchorLabel} file found; deriving ${toolDisplayName} project name from ${secondaryAnchorLabel ?? 'sidecar file'}. Upload the ${primaryAnchorLabel} file for a complete project.`);
            anchors = fallback;
        }
    }
    if (anchors.length === 0) {
        warnings.push(`No ${toolDisplayName} project file (${primaryAnchorLabel}${secondaryAnchorLabel ? ` or ${secondaryAnchorLabel}` : ''}) found. Upload the project file for a complete project definition.`);
        return {
            projects: [buildProjectDefinition(tool, undefined, toolFiles, blobMap)],
            warnings,
        };
    }
    const groups = anchors.map((anchor) => ({
        name: projectDisplayName(anchor.relativePath),
        homeDir: dirnameForward(anchor.relativePath),
        files: [],
    }));
    for (const detail of toolFiles) {
        const fileDir = dirnameForward(detail.relativePath);
        let best;
        let bestLen = -1;
        for (const group of groups) {
            if (isSameOrDescendantDir(fileDir, group.homeDir) && group.homeDir.length > bestLen) {
                best = group;
                bestLen = group.homeDir.length;
            }
        }
        (best ?? groups[0]).files.push(detail);
    }
    const projects = groups.map((g) => buildProjectDefinition(tool, g.name, g.files, blobMap));
    return { projects, warnings };
}
//# sourceMappingURL=project-detection.js.map